import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { UserCheck, MapPinned, Rocket, Clock } from "lucide-react";

/* ======================================================
   PAGE WRAPPER
====================================================== */

const Page = styled.main`
  background: ${({ theme }) => theme.colors.bg};
  overflow-x: hidden;
  margin-top: -72px;
`;

/* ======================================================
   TEXT HIGHLIGHT
====================================================== */

const Highlight = styled.span`
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 900;
`;

/* ======================================================
   HERO SECTION
====================================================== */

const Hero = styled.section`
  padding: 220px 20px 160px;
  text-align: center;
  background: radial-gradient(
      1000px at 50% -10%,
      rgba(59, 130, 246, 0.18),
      transparent 60%
    ),
    ${({ theme }) => theme.colors.bg};
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const HeroSub = styled(motion.p)`
  max-width: 820px;
  margin: 26px auto 0;
  font-size: 20px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   HOW IT WORKS
====================================================== */

const Section = styled.section`
  position: relative;
  padding: 180px 20px;
  background: radial-gradient(
      900px at 50% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 55%
    ),
    ${({ theme }) => theme.colors.bg};
`;

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

const Heading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(34px, 4.5vw, 50px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 760px;
  margin: 22px auto 0;
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StepsWrap = styled.div`
  position: relative;
  margin-top: 120px;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Connector = styled.div`
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  height: 2px;
  background-image: linear-gradient(
    to right,
    rgba(59, 130, 246, 0.35) 40%,
    rgba(59, 130, 246, 0) 0%
  );
  background-size: 16px 2px;
  animation: dash 6s linear infinite;

  @keyframes dash {
    to {
      background-position: 200px 0;
    }
  }

  @media (max-width: 1000px) {
    display: none;
  }
`;

const StepCard = styled(motion.div)`
  position: relative;
  padding: 52px 42px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.65);
`;

const StepNumber = styled.div`
  position: absolute;
  top: -28px;
  left: 38px;
  font-size: 72px;
  font-weight: 900;
  opacity: 0.08;
  color: ${({ theme }) => theme.colors.text};
`;

const ClockWrap = styled(motion.div)`
  position: absolute;
  top: 18px;
  right: 18px;
  opacity: 0.08;
`;

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  margin-bottom: 28px;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: #fff;
`;

const Title = styled.h4`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   PREMIUM WHAT YOU GET
====================================================== */

const Benefits = styled.section`
  padding: 180px 20px;
  background: radial-gradient(
      800px at 50% 0%,
      rgba(59, 130, 246, 0.12),
      transparent 60%
    ),
    ${({ theme }) => theme.colors.bgElevated};
`;

const BenefitsHeader = styled.div`
  text-align: center;
  max-width: 820px;
  margin: auto;
`;

const BenefitsSub = styled.p`
  margin-top: 20px;
  font-size: 18px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Grid = styled.div`
  max-width: 1100px;
  margin: 100px auto 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 36px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  padding: 42px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.035),
    rgba(255, 255, 255, 0.015)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 30px 90px rgba(2, 6, 23, 0.65);
`;

const BenefitTitle = styled.h4`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   DATA & ANIMATIONS
====================================================== */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const STEPS = [
  {
    icon: UserCheck,
    title: "Understand where you stand",
    text: (
      <>
        We understand your <Highlight>skills</Highlight>,{" "}
        <Highlight>goals</Highlight>, time limits, and{" "}
        <Highlight>confusion</Highlight> — before suggesting anything.
      </>
    ),
  },
  {
    icon: MapPinned,
    title: "Get a clear career roadmap",
    text: (
      <>
        A <Highlight>structured</Highlight>, realistic plan that tells you
        exactly what to <Highlight>learn</Highlight>,{" "}
        <Highlight>build</Highlight>, and <Highlight>practice</Highlight>.
      </>
    ),
  },
  {
    icon: Rocket,
    title: "Execute daily with confidence",
    text: (
      <>
        No more <Highlight>guessing</Highlight>. Every day you know what{" "}
        <Highlight>matters</Highlight> and what doesn’t.
      </>
    ),
  },
];

const BENEFITS = [
  {
    title: "A roadmap made only for you",
    text: (
      <>
        skilll.dev creates a <Highlight>path</Highlight> based on where you are
        today and where you want to <Highlight>reach</Highlight> — without{" "}
        <Highlight>overwhelm</Highlight>.
      </>
    ),
  },
  {
    title: "Daily clarity, zero confusion",
    text: (
      <>
        You always know what to do <Highlight>today</Highlight>. What to{" "}
        <Highlight>learn</Highlight>. What to <Highlight>build</Highlight>. What
        to <Highlight>skip</Highlight>.
      </>
    ),
  },
  {
    title: "Support when you feel stuck",
    text: (
      <>
        skilll.dev is a <Highlight>support system</Highlight> — so you never
        feel <Highlight>alone</Highlight> again.
      </>
    ),
  },
  {
    title: "Progress you can actually feel",
    text: (
      <>
        Clear <Highlight>milestones</Highlight>, visible{" "}
        <Highlight>growth</Highlight>, and growing{" "}
        <Highlight>confidence</Highlight> in your journey.
      </>
    ),
  },
];

/* ======================================================
   PAGE
====================================================== */

export default function HowItWorksPage() {
  return (
    <Page>
      <Hero>
        <HeroTitle variants={fadeUp} initial="hidden" animate="show">
          Your <Highlight>Career</Highlight>, Finally{" "}
          <Highlight>Systemized</Highlight>
        </HeroTitle>
        <HeroSub variants={fadeUp} initial="hidden" animate="show">
          skilll.dev removes <Highlight>confusion</Highlight> from learning,
          building, and growing — and gives you a <Highlight>system</Highlight>{" "}
          that truly <Highlight>supports</Highlight> you.
        </HeroSub>
      </Hero>

      <Section>
        <Container>
          <Heading variants={fadeUp} initial="hidden" whileInView="show">
            How skilll.dev Works
          </Heading>
          <Sub variants={fadeUp} initial="hidden" whileInView="show">
            No <Highlight>overload</Highlight>. No{" "}
            <Highlight>confusion</Highlight>.
            <br />
            Just <Highlight>clarity</Highlight>,{" "}
            <Highlight>structure</Highlight>, and <Highlight>support</Highlight>
            .
          </Sub>

          <StepsWrap>
            <Connector />
            <Steps>
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <StepCard key={i} whileHover={{ y: -14 }}>
                    <StepNumber>{`0${i + 1}`}</StepNumber>
                    <ClockWrap
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 40,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                    >
                      <Clock size={22} />
                    </ClockWrap>
                    <IconWrap>
                      <Icon size={30} />
                    </IconWrap>
                    <Title>{step.title}</Title>
                    <Text>{step.text}</Text>
                  </StepCard>
                );
              })}
            </Steps>
          </StepsWrap>
        </Container>
      </Section>

      <Benefits>
        <BenefitsHeader>
          <Heading variants={fadeUp} initial="hidden" whileInView="show">
            What You Get with skilll.dev
          </Heading>
          <BenefitsSub>
            This is not just a <Highlight>platform</Highlight>.
            <br />
            It’s a <Highlight>support system</Highlight> built for moments when
            you feel <Highlight>confused</Highlight>,{" "}
            <Highlight>stuck</Highlight>, or <Highlight>unsure</Highlight>.
          </BenefitsSub>
        </BenefitsHeader>

        <Grid>
          {BENEFITS.map((b, i) => (
            <BenefitCard
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              whileHover={{ y: -12 }}
            >
              <BenefitTitle>{b.title}</BenefitTitle>
              <BenefitText>{b.text}</BenefitText>
            </BenefitCard>
          ))}
        </Grid>
      </Benefits>
    </Page>
  );
}
